/**
 * Dashboard — Specification §7, §10 and §11.
 *
 * One page, three audiences. The API decides which figures come back from the
 * caller's authorization scope, so an Admin sees system-wide totals, a Manager
 * sees their office, and a Regular User sees their own. The client does not
 * choose, and cannot.
 *
 * Every figure is computed from records (DASH-001). A rate with no denominator
 * shows an em dash rather than 0% — an office that has sent nothing has no
 * response rate, and "0%" would read as a failing office rather than an idle
 * one.
 */

import { useQuery } from '@tanstack/react-query';
import { formatDuration, formatRate } from '@lto/contracts';

import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import { ErrorState, Metric, Panel } from '../components/ui';

interface Metrics {
  readonly total: number;
  readonly unread: number;
  readonly awaitingReply: number;
  readonly closed: number;
  readonly sentToday: number;
  readonly receivedToday: number;
  readonly repliedToday: number;
  readonly overdue: number;
  readonly averageReadSeconds: number | null;
  readonly averageResponseSeconds: number | null;
  readonly averageHandlingSeconds: number | null;
  readonly averageDeliverySeconds: number | null;
  readonly responseRate: number | null;
  readonly readRate: number | null;
  readonly closureRate: number | null;
}

interface DashboardResponse {
  readonly scope: 'system' | 'office' | 'user';
  readonly metrics: Metrics;
  readonly slaBuckets: { under8h: number; from8to24h: number; over24h: number };
}

interface OfficeRow {
  readonly officeId: string;
  readonly officeName: string;
  readonly officeCode: string;
  readonly total: number;
  readonly unread: number;
  readonly pending: number;
  readonly replied: number;
  readonly overdue: number;
  readonly averageResponseSeconds: number | null;
  readonly responseRate: number | null;
}

const SCOPE_TITLE: Record<DashboardResponse['scope'], string> = {
  system: 'System overview',
  office: 'Office overview',
  user: 'My confirmations',
};

const SCOPE_HINT: Record<DashboardResponse['scope'], string> = {
  system: 'Every office',
  office: 'Your office, sent and received',
  user: 'Confirmations you take part in',
};

function MetricSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" aria-busy="true">
      <span className="sr-only">Loading figures…</span>
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          className="border px-4 py-3"
          style={{
            background: 'var(--color-surface)',
            borderColor: 'var(--color-rule)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <div className="skeleton h-2.5 w-2/3" />
          <div className="skeleton mt-3 h-5 w-1/3" />
          <div className="skeleton mt-2.5 h-2 w-4/5" />
        </div>
      ))}
    </div>
  );
}

export function Dashboard() {
  const { user, can } = useSession();

  const dashboard = useQuery({
    queryKey: ['dashboard', 'me'],
    queryFn: () => api.get<DashboardResponse>('/api/v1/dashboard/me'),
  });

  // §8 is only meaningful to someone who can see more than one office.
  const offices = useQuery({
    queryKey: ['dashboard', 'offices'],
    queryFn: () => api.get<OfficeRow[]>('/api/v1/dashboard/offices'),
    enabled: can('report.view'),
  });

  const greeting =
    user !== null && user.firstName !== ''
      ? `${user.firstName} ${user.lastName}`.trim()
      : 'Dashboard';

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold tracking-tight">{greeting}</h1>
        {dashboard.isSuccess && (
          <p className="text-sm" style={{ color: 'var(--color-ink-subtle)' }}>
            {SCOPE_TITLE[dashboard.data.scope]} · {SCOPE_HINT[dashboard.data.scope]}
          </p>
        )}
      </div>

      {dashboard.isPending && <MetricSkeleton />}

      {dashboard.isError && (
        <Panel>
          <ErrorState
            message={
              dashboard.error instanceof ApiError
                ? (dashboard.error.problem?.detail ?? 'Could not load your dashboard.')
                : 'Could not load your dashboard.'
            }
            onRetry={() => void dashboard.refetch()}
          />
        </Panel>
      )}

      {dashboard.isSuccess && (
        <>
          {/* Headline counts — §7, §10 and §11 all open with these four. */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Metric
              label="Total confirmations"
              value={dashboard.data.metrics.total.toLocaleString()}
              hint="Excluding drafts"
            />
            <Metric
              label="Unread"
              value={dashboard.data.metrics.unread.toLocaleString()}
              hint={dashboard.data.scope === 'user' ? 'Messages you have not opened' : 'Sent but never opened'}
              tone={dashboard.data.metrics.unread > 0 ? 'attention' : 'neutral'}
            />
            <Metric
              label="Awaiting reply"
              value={dashboard.data.metrics.awaitingReply.toLocaleString()}
              hint="Someone owes a response"
              tone={dashboard.data.metrics.awaitingReply > 0 ? 'attention' : 'neutral'}
            />
            <Metric
              label="Closed"
              value={dashboard.data.metrics.closed.toLocaleString()}
              hint="Completed and filed"
            />
          </div>

          {/* Today's activity, plus the one figure that needs acting on. */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Metric label="Sent today" value={dashboard.data.metrics.sentToday.toLocaleString()} />
            <Metric label="Received today" value={dashboard.data.metrics.receivedToday.toLocaleString()} />
            <Metric label="Replied today" value={dashboard.data.metrics.repliedToday.toLocaleString()} />
            <Metric
              label="Overdue"
              value={dashboard.data.metrics.overdue.toLocaleString()}
              hint="Past deadline, unanswered"
              tone={dashboard.data.metrics.overdue > 0 ? 'attention' : 'neutral'}
            />
          </div>

          <div className="grid gap-3 lg:grid-cols-2">
            <Panel title="Response performance" description="Averaged across the whole period">
              <dl className="divide-y" style={{ borderColor: 'var(--color-rule)' }}>
                {[
                  {
                    label: 'Average delivery time',
                    value: formatDuration(dashboard.data.metrics.averageDeliverySeconds),
                    note: 'Sent → received by office',
                  },
                  {
                    label: 'Average read time',
                    value: formatDuration(dashboard.data.metrics.averageReadSeconds),
                    note: 'Received → opened',
                  },
                  {
                    label: 'Average handling time',
                    value: formatDuration(dashboard.data.metrics.averageHandlingSeconds),
                    note: 'Opened → replied',
                  },
                  {
                    label: 'Average response time',
                    value: formatDuration(dashboard.data.metrics.averageResponseSeconds),
                    note: 'Sent → replied — the SLA measure',
                  },
                ].map((row) => (
                  <div key={row.label} className="flex items-baseline justify-between gap-4 px-4 py-2.5">
                    <dt>
                      <span className="text-sm font-medium">{row.label}</span>
                      <span className="ml-2 text-xs" style={{ color: 'var(--color-ink-subtle)' }}>
                        {row.note}
                      </span>
                    </dt>
                    <dd className="tabular text-md font-semibold whitespace-nowrap">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </Panel>

            <Panel title="Rates" description="A dash means there is nothing to measure yet">
              <dl className="divide-y" style={{ borderColor: 'var(--color-rule)' }}>
                {[
                  { label: 'Response rate', value: dashboard.data.metrics.responseRate, note: 'Replied ÷ sent' },
                  { label: 'Read rate', value: dashboard.data.metrics.readRate, note: 'Opened ÷ received' },
                  { label: 'Closure rate', value: dashboard.data.metrics.closureRate, note: 'Closed ÷ sent' },
                ].map((row) => (
                  <div key={row.label} className="px-4 py-2.5">
                    <div className="flex items-baseline justify-between gap-4">
                      <dt>
                        <span className="text-sm font-medium">{row.label}</span>
                        <span className="ml-2 text-xs" style={{ color: 'var(--color-ink-subtle)' }}>
                          {row.note}
                        </span>
                      </dt>
                      <dd className="tabular text-md font-semibold">{formatRate(row.value)}</dd>
                    </div>
                    {/* A bar rather than a chart: one dimension, no axes needed. */}
                    {row.value !== null && (
                      <div
                        className="mt-1.5 h-1 w-full overflow-hidden"
                        style={{ background: 'var(--color-rule)', borderRadius: '1px' }}
                      >
                        <div
                          className="h-full"
                          style={{
                            width: `${Math.min(100, row.value)}%`,
                            background: 'var(--color-accent)',
                            transition: 'width var(--duration-slow) var(--ease-out)',
                          }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </dl>
            </Panel>
          </div>

          {/* SLA ageing — §14. Only shown when something is actually waiting. */}
          {(() => {
            const b = dashboard.data.slaBuckets;
            const waiting = b.under8h + b.from8to24h + b.over24h;
            if (waiting === 0) return null;
            return (
              <Panel title="Awaiting response" description="How long unanswered confirmations have been waiting">
                <div className="grid grid-cols-3 gap-px" style={{ background: 'var(--color-rule)' }}>
                  {[
                    { label: 'Under 8 hours', value: b.under8h, tone: 'var(--color-ink)' },
                    { label: '8 – 24 hours', value: b.from8to24h, tone: 'var(--color-warning)' },
                    { label: 'Over 24 hours', value: b.over24h, tone: 'var(--color-danger)' },
                  ].map((bucket) => (
                    <div key={bucket.label} className="px-4 py-3" style={{ background: 'var(--color-surface)' }}>
                      <div className="text-xs uppercase tracking-wider" style={{ color: 'var(--color-ink-subtle)' }}>
                        {bucket.label}
                      </div>
                      <div className="tabular mt-1 text-xl font-semibold" style={{ color: bucket.tone }}>
                        {bucket.value}
                      </div>
                    </div>
                  ))}
                </div>
              </Panel>
            );
          })()}

          {/* Office comparison — §8. */}
          {can('report.view') && offices.isSuccess && offices.data.length > 0 && (
            <Panel
              title="Office performance"
              description="Measured on what each office receives, not what it sends"
            >
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left">
                  <thead className="sticky-head">
                    <tr
                      className="border-b text-xs font-medium tracking-wider uppercase"
                      style={{ borderColor: 'var(--color-rule)', color: 'var(--color-ink-subtle)' }}
                    >
                      <th scope="col" className="px-4 py-2">Office</th>
                      <th scope="col" className="px-4 py-2 text-right">Total</th>
                      <th scope="col" className="px-4 py-2 text-right">Unread</th>
                      <th scope="col" className="px-4 py-2 text-right">Pending</th>
                      <th scope="col" className="px-4 py-2 text-right">Replied</th>
                      <th scope="col" className="px-4 py-2 text-right">Overdue</th>
                      <th scope="col" className="px-4 py-2 text-right">Avg response</th>
                      <th scope="col" className="px-4 py-2 text-right">Rate</th>
                    </tr>
                  </thead>
                  <tbody className="stagger">
                    {offices.data.map((row) => (
                      <tr
                        key={row.officeId}
                        className="row-hover border-b last:border-b-0"
                        style={{ borderColor: 'var(--color-rule)' }}
                      >
                        <td className="px-4 py-2">
                          <span className="font-medium">{row.officeName}</span>
                          <span className="reference ml-2 text-xs" style={{ color: 'var(--color-ink-subtle)' }}>
                            {row.officeCode}
                          </span>
                        </td>
                        <td className="tabular px-4 py-2 text-right">{row.total}</td>
                        <td className="tabular px-4 py-2 text-right">{row.unread}</td>
                        <td className="tabular px-4 py-2 text-right">{row.pending}</td>
                        <td className="tabular px-4 py-2 text-right">{row.replied}</td>
                        <td
                          className="tabular px-4 py-2 text-right font-medium"
                          style={{ color: row.overdue > 0 ? 'var(--color-danger)' : undefined }}
                        >
                          {row.overdue}
                        </td>
                        <td className="tabular px-4 py-2 text-right whitespace-nowrap">
                          {formatDuration(row.averageResponseSeconds)}
                        </td>
                        <td className="tabular px-4 py-2 text-right">{formatRate(row.responseRate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          )}
        </>
      )}
    </div>
  );
}
